import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useBranding } from "@/lib/branding";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { Calendar, Clock, CheckCircle2, ArrowRight, AlertCircle } from "lucide-react";
import PoweredBy from "@/components/PoweredBy";

const fmtDate = (iso) => new Date(iso).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
const fmtTime = (iso) => new Date(iso).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });

export default function Reschedule() {
  const { branding } = useBranding();
  const nav = useNavigate();
  const { token } = useParams();

  const [state, setState] = useState({ loading: true, error: null, current: null });
  const [slots, setSlots] = useState([]);
  const [picked, setPicked] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [cur, list] = await Promise.all([
          api.get(`/appointments/reschedule/${token}`),
          api.get("/appointments/slots"),
        ]);
        if (cancelled) return;
        setState({ loading: false, error: null, current: cur.data.current });
        setSlots(list.data);
      } catch (e) {
        if (cancelled) return;
        setState({ loading: false, error: e?.response?.data?.detail || "Could not open your reschedule link", current: null });
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const byDay = useMemo(() => {
    const map = new Map();
    for (const s of slots) {
      const key = new Date(s.start_at).toDateString();
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(s);
    }
    return Array.from(map.entries());
  }, [slots]);

  const submit = async () => {
    if (!picked) return;
    setSubmitting(true);
    try {
      const r = await api.post(`/appointments/reschedule/${token}`, { new_slot_id: picked.id });
      setDone(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reschedule failed");
      // Refresh slot list in case something got booked mid-flight
      try {
        const l = await api.get("/appointments/slots");
        setSlots(l.data);
      } catch { /* ignore */ }
      setPicked(null);
    } finally { setSubmitting(false); }
  };

  if (state.loading) {
    return <FullShell><Card className="p-8 text-center text-muted-foreground" data-testid="reschedule-loading">Loading your appointment…</Card></FullShell>;
  }

  if (state.error) {
    return (
      <FullShell branding={branding}>
        <Card className="p-8 text-center max-w-md mx-auto" data-testid="reschedule-error">
          <AlertCircle size={40} className="mx-auto text-destructive mb-3" />
          <h1 className="font-serif text-2xl mb-2">Can't open this link</h1>
          <p className="text-sm text-muted-foreground mb-6">{state.error}</p>
          <Button asChild><Link to="/book">Pick a fresh time <ArrowRight size={12} className="ml-1" /></Link></Button>
        </Card>
      </FullShell>
    );
  }

  if (done) {
    return (
      <FullShell branding={branding}>
        <Card className="p-10 text-center max-w-md mx-auto" data-testid="reschedule-success">
          <CheckCircle2 size={56} className="mx-auto text-primary mb-4" />
          <div className="label-caps mb-2">Rescheduled</div>
          <h1 className="font-serif text-4xl mb-3">You're on the new time.</h1>
          <div className="rounded-md border border-primary/40 bg-primary/5 p-4 mb-6 text-left">
            <div className="text-sm">
              <div className="flex items-center gap-2 mb-1"><Calendar size={14} className="text-primary" /> {fmtDate(done.start_at)}</div>
              <div className="flex items-center gap-2"><Clock size={14} className="text-primary" /> {fmtTime(done.start_at)} · {done.duration_min} min</div>
            </div>
          </div>
          <p className="text-xs text-muted-foreground mb-6">
            We just sent a fresh confirmation email with an updated calendar invite. The old one on your calendar is superseded.
          </p>
          <Button onClick={() => nav("/")} className="w-full" data-testid="reschedule-done-btn">Back to homepage</Button>
        </Card>
      </FullShell>
    );
  }

  const current = state.current;

  return (
    <FullShell branding={branding}>
      <div className="max-w-3xl mx-auto space-y-6">
        <div>
          <div className="label-caps mb-2">Reschedule</div>
          <h1 className="font-serif text-4xl lg:text-5xl leading-none mb-3">Pick a new time.</h1>
          {current && (
            <p className="text-muted-foreground text-sm">
              Your current appointment is <strong>{fmtDate(current.start_at)} at {fmtTime(current.start_at)}</strong> ({current.duration_min} min).
              Pick a different slot below and we'll move you over — old time frees up automatically.
            </p>
          )}
        </div>

        {slots.length === 0 ? (
          <Card className="p-8 text-center text-muted-foreground" data-testid="reschedule-empty">
            No other open times right now. Please reply to your confirmation email and we'll find something together.
          </Card>
        ) : (
          <Card className="p-6">
            <div className="label-caps text-[10px] mb-4">Available slots</div>
            <div className="space-y-5" data-testid="reschedule-slot-grid">
              {byDay.map(([day, list]) => (
                <div key={day}>
                  <div className="text-sm font-medium mb-2">{new Date(day).toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" })}</div>
                  <div className="flex flex-wrap gap-2">
                    {list.map((s) => (
                      <button
                        type="button"
                        key={s.id}
                        onClick={() => setPicked(s)}
                        data-testid={`reschedule-slot-${s.id}`}
                        className={`text-xs px-3 py-2 rounded-md border transition-colors ${
                          picked?.id === s.id
                            ? "bg-primary text-primary-foreground border-primary"
                            : "bg-background border-border hover:border-primary"
                        }`}
                      >
                        {fmtTime(s.start_at)} · {s.duration_min}m
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}

        {picked && (
          <div className="flex justify-between items-center gap-3">
            <div className="text-sm text-muted-foreground">
              Moving to <strong className="text-foreground">{fmtDate(picked.start_at)} at {fmtTime(picked.start_at)}</strong>
            </div>
            <Button size="lg" onClick={submit} disabled={submitting} data-testid="reschedule-submit-btn">
              {submitting ? "Moving you over…" : "Confirm new time"}
            </Button>
          </div>
        )}

        <PoweredBy className="text-center mt-8" />
      </div>
    </FullShell>
  );
}

function FullShell({ branding, children }) {
  return (
    <div className="min-h-screen bg-background paper-texture">
      <header className="border-b border-border bg-card">
        <div className="max-w-4xl mx-auto p-6 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            {branding?.logo_url ? <img src={branding.logo_url} alt="" className="h-9 w-9 object-contain" /> :
              <div className="h-9 w-9 rounded-md bg-primary text-primary-foreground grid place-items-center font-serif">{branding?.brand_name?.[0] || "D"}</div>}
            <div className="font-serif text-2xl">{branding?.brand_name || "DyNamic Tax"}</div>
          </Link>
          <div className="label-caps hidden sm:block">Reschedule</div>
        </div>
      </header>
      <section className="max-w-4xl mx-auto p-6 lg:p-12">{children}</section>
    </div>
  );
}
